import { useLayoutEffect, useRef, useState, type MutableRefObject } from 'react'
import {
  anchorToRemeasure,
  initialFollowState,
  isAtBottom,
  onToolToggled,
  onUserScrolled,
  shouldStickToBottom,
} from './scroll-follow'

// 捲動跟隨的 DOM 這一側（#1404）。純函式在 scroll-follow.ts，這裡只負責時序：
// 什麼時候量、什麼時候補、以及那段位移是不是使用者自己捲的。

export interface ScrollFollowHandles {
  /** 掛在捲動容器上。 */
  threadRef: MutableRefObject<HTMLElement | null>
  /** 掛在捲動容器的 onScroll。 */
  handleThreadScroll: () => void
  /** 工具紀錄展開／收合時回報。 */
  handleToolToggle: (id: string, open: boolean) => void
  /** 此刻正在手動錨定。容器要靠它關掉瀏覽器原生錨定，兩者疊加會推兩次。 */
  anchored: boolean
}

// 逐一比對而不是拼選擇器：id 來自 toolCallId，帶 : 之類的字元就得跑跳脫，
// 而掛著 data-anchor-id 的只有工具卡，數量很少。
const findAnchor = (el: HTMLElement, id: string): Element | null => {
  const nodes = el.querySelectorAll('[data-anchor-id]')
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getAttribute('data-anchor-id') === id) return nodes[i]
  }
  return null
}

/** 錨定的那一則距離容器頂端多遠。找不到節點回 null（還沒畫出來）。 */
const anchorOffset = (el: HTMLElement, id: string): number | null => {
  const node = findAnchor(el, id)
  return node ? node.getBoundingClientRect().top - el.getBoundingClientRect().top : null
}

/**
 * deps ＝ 會讓內容長高的東西（messages、status）。錨定補正要跟著它重跑，
 * 才能在同一個 layout pass、paint 之前把視窗移回原位。
 */
export const useScrollFollow = (deps: readonly unknown[]): ScrollFollowHandles => {
  const threadRef = useRef<HTMLElement | null>(null)
  const [follow, setFollow] = useState(initialFollowState)
  // 事件處理器要同步讀得到最新狀態，不能等下一次 render
  const followRef = useRef(follow)
  followRef.current = follow
  // 錨定的那一則距離視窗頂端多遠。之後每次重繪都把它移回這個位置。
  const anchorOffsetRef = useRef(0)
  // 我們自己最後設到的 scrollTop。跟現值一比就知道中間使用者動了多少。
  const lastTopRef = useRef(0)

  const scrollTo = (el: HTMLElement, next: number) => {
    el.scrollTop = next
    lastTopRef.current = el.scrollTop
  }

  /**
   * 🔴 內容在錨點上方長高不會動到 scrollTop，所以 scrollTop 的變化扣掉我們自己
   * 設的，剩下的必然是使用者捲的——把它吸進基準，下一次補正才不會抵銷掉他。
   * 用位置差而不是靠捲動事件辨識：事件一幀才派發一次，chunk 插得進去。
   */
  const absorbUserScroll = (el: HTMLElement) => {
    const delta = el.scrollTop - lastTopRef.current
    lastTopRef.current = el.scrollTop
    if (delta !== 0 && followRef.current.anchorId !== null) anchorOffsetRef.current -= delta
  }

  const handleToolToggle = (id: string, open: boolean) => {
    const el = threadRef.current
    if (el) absorbUserScroll(el)
    const before = followRef.current
    const after = onToolToggled(before, id, open)
    // 錨點換人才量（含收合後退回前一則）。錨點沒換就再量一次的話，
    // 量到的會是已經錨好的位置，等於把錨點洗掉。
    const target = anchorToRemeasure(before, after)
    if (el && target !== null) {
      const offset = anchorOffset(el, target)
      if (offset !== null) anchorOffsetRef.current = offset
    }
    setFollow(after)
  }

  const handleThreadScroll = () => {
    const el = threadRef.current
    if (!el) return
    absorbUserScroll(el)
    // 狀態實質未變時回傳的是原物件，版面效果不會被自己的捲動再叫醒（#1383）
    setFollow((state) => onUserScrolled(state, isAtBottom(el)))
  }

  // 直接設 scrollTop 而非 smooth scrollIntoView：串流時內容一直長高，平滑捲動
  // 追不上。讓位時改成把錨定的那一則固定在原位，新內容照長但視窗不跳。
  useLayoutEffect(() => {
    const el = threadRef.current
    if (!el) return
    // 🔴 先吸收：使用者的捲動事件可能還沒輪到派發，chunk 就已經進到這裡了
    absorbUserScroll(el)
    if (shouldStickToBottom(follow)) {
      scrollTo(el, el.scrollHeight)
      return
    }
    if (!follow.anchorId) return
    // TODO(#1404)：錨點節點不見了（換對話、重新整理）目前只是不補正，狀態仍停在
    // anchored、跟隨回不來。既有行為，修它要一併動 scroll-follow 的狀態機。
    const offset = anchorOffset(el, follow.anchorId)
    if (offset !== null) scrollTo(el, el.scrollTop + offset - anchorOffsetRef.current)
    // deps 長度固定（呼叫端傳的是 messages／status），展開只是把它們接進來
  }, [follow, ...deps])

  return { threadRef, handleThreadScroll, handleToolToggle, anchored: follow.anchorId !== null }
}
